import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Chip,
    Divider,
    Grid,
    Stack,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';

/**
 * Casca PADRÃO do painel de busca/filtros das páginas.
 *
 * Existe para as telas pararem de divergir: antes cada página tinha sua própria casca
 * (Accordion, Paper ou nenhuma), seu próprio jeito de paginar (MUI Pagination x botões
 * "Anterior/Próxima") e nem todas tinham "Limpar". Aqui o esqueleto é um só; cada
 * página entrega apenas OS SEUS campos como children (em <Grid item>).
 *
 * A paginação NÃO vive aqui — ela é o <PaginacaoFooter> no fim da lista. Já esteve
 * neste painel, e na prática obrigava a rolar de volta ao topo só para avançar de
 * página, logo depois de percorrer a lista inteira.
 *
 * Uso:
 *   <FiltrosPanel
 *       ativos={activeFilterCount}
 *       loading={loading} onLimpar={limpar} onBuscar={buscar}
 *   >
 *       <Grid item xs={12} sm={6} md={3}> ...campo... </Grid>
 *   </FiltrosPanel>
 */
export default function FiltrosPanel({
    children,
    titulo = 'Busca e Filtros',
    ativos = 0,
    defaultExpanded = false,
    // ── ações ──
    onLimpar,
    onBuscar,
    buscarLabel = 'Buscar',
    loading = false,
    // ── customização pontual (ex.: modo Lixeira) ──
    destaque = false,
    tituloAlternativo,
    icone,
    acoesExtras = null,
    faixaSuperior = null,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const cor = destaque ? theme.palette.error.main : theme.palette.action.active;

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            elevation={0}
            sx={{
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: destaque ? 'error.light' : 'divider',
                '&:before': { display: 'none' },
                backgroundColor: destaque
                    ? alpha(theme.palette.error.main, 0.08)
                    : 'inherit',
            }}
        >
            <AccordionSummary expandIcon={<Lucide.ChevronDown size={20} />}>
                <Stack direction="row" spacing={1} alignItems="center">
                    {icone || <Lucide.ListFilter color={cor} />}
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        color={destaque ? 'error' : 'inherit'}
                    >
                        {destaque && tituloAlternativo ? tituloAlternativo : titulo}
                    </Typography>
                    {ativos > 0 && (
                        <Chip
                            label={`${ativos} ativo(s)`}
                            size="small"
                            color={destaque ? 'error' : 'primary'}
                            variant="outlined"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0 }}>
                {faixaSuperior}

                <Grid container spacing={2} alignItems="center">
                    {children}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent={{ xs: 'stretch', md: 'flex-end' }}
                    flexWrap="wrap"
                    useFlexGap
                >
                        {acoesExtras}

                        {typeof onLimpar === 'function' && (
                            <Button
                                onClick={onLimpar}
                                disabled={loading}
                                color="inherit"
                                sx={{ borderRadius: 2 }}
                                fullWidth={isMobile}
                            >
                                Limpar
                            </Button>
                        )}

                        {typeof onBuscar === 'function' && (
                            <Button
                                onClick={onBuscar}
                                disabled={loading}
                                variant="contained"
                                disableElevation
                                startIcon={<Lucide.Search size={18} />}
                                sx={{ borderRadius: 2, minWidth: 132 }}
                                fullWidth={isMobile}
                            >
                                {buscarLabel}
                            </Button>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}

/** Larguras padrão de um campo de filtro — use para manter o alinhamento entre páginas. */
export const CAMPO = { xs: 12, sm: 6, md: 3 };

/** Meia largura (campos curtos como Direção/Ordenação em telas grandes). */
export const CAMPO_CURTO = { xs: 12, sm: 6, md: 2 };

/** Campo largo (busca principal). */
export const CAMPO_LARGO = { xs: 12, sm: 12, md: 6 };
